import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
// material-ui
import { useTheme } from '@mui/material/styles';
import { Box, Drawer, useMediaQuery } from '@mui/material';

// third-party
import PerfectScrollbar from 'react-perfect-scrollbar';
import { BrowserView, MobileView } from 'react-device-detect';

// project imports
import MenuList from './MenuList';
import LogoSection from '../LogoSection';
import { drawerWidth } from '../../../store/constant';
import FluxSidebarEntry, { fluxEntryUrl, FLUX_ENTRY_HEIGHT } from './FluxSidebarEntry';

// JWTContext içinde ekran genişliğine göre body'ye uygulanan manuel zoom'u
// (örn. "77%", "65%") okuyup sayısal değere çevirir. Zoom yoksa 1 döner.
const readBodyZoom = () => {
    const raw = document.body.style.zoom;
    if (!raw) return 1;
    const str = String(raw).trim();
    if (str.endsWith('%')) {
        const n = parseFloat(str.slice(0, -1));
        return isNaN(n) || n === 0 ? 1 : n / 100;
    }
    const n = parseFloat(str);
    return isNaN(n) || n === 0 ? 1 : n;
};

// Component içindeki `window` prop'u global window'u gölgelediği için
// gerçek (tarayıcı) window referansını modül seviyesinde yakalıyoruz.
const rootWindow = typeof window !== 'undefined' ? window : undefined;

// flux_doctor me API'sinin (`/api2/v2/accunt/me/`) user payload'undan gelir; MenuList açılışta
// fetch edip localStorage userData'ya yazar ve 'userDataUpdated' eventi basar. Anahtar yoksa /
// bozuksa buton "henüz verilmedi" sayılır ve çizilmez.
const readFluxDoctorFlag = () => {
    try {
        return JSON.parse(rootWindow.localStorage.getItem('userData') || '{}').flux_doctor === true;
    } catch (e) {
        return false;
    }
};

// ==============================|| SIDEBAR DRAWER ||============================== //

const Sidebar = ({ drawerOpen, drawerToggle, window }) => {
    const theme = useTheme();
    const matchUpMd = useMediaQuery(theme.breakpoints.up('md'));
    const [bodyZoom, setBodyZoom] = useState(readBodyZoom());

    useEffect(() => {
        const updateZoom = () => setBodyZoom(readBodyZoom());
        updateZoom();
        if (!rootWindow) return;
        // Zoom, JWTContext içinde resize ve doğrudan style atamasıyla değişebilir;
        // hem resize'ı dinleriz hem de body style değişimini gözlemleyiz.
        rootWindow.addEventListener('resize', updateZoom);
        const observer = new MutationObserver(updateZoom);
        observer.observe(document.body, { attributes: true, attributeFilter: ['style'] });
        return () => {
            rootWindow.removeEventListener('resize', updateZoom);
            observer.disconnect();
        };
    }, []);

    // Body zoom uygulandığında layout pikseli ile ekrandaki piksel ayrışır.
    // Header (88px/56px) de zoom'lanır; PerfectScrollbar'ın görsel olarak ekranı
    // tam doldurması için layout-piksel yüksekliği: (100vh / zoom) - headerPx
    const headerHeight = matchUpMd ? 88 : 56;
    // "FLUX'a geç" under the menu (owner, 2026-10-29): URL from <meta name="orthero-flux-entry">, read once;
    // null (no button, menu as tall as before) when the shell did not render the meta or inside an iframe.
    // Called through the module function, not inline: `window` is shadowed by this component's prop.
    // Visibility also needs flux_doctor=true from the me API: MenuList merges it into userData and
    // fires 'userDataUpdated', so a session older than the key appears (correctly) once that lands.
    const fluxUrl = useMemo(fluxEntryUrl, []);
    const [fluxDoctor, setFluxDoctor] = useState(readFluxDoctorFlag);
    useEffect(() => {
        const refresh = () => setFluxDoctor(readFluxDoctorFlag());
        rootWindow.addEventListener('userDataUpdated', refresh);
        return () => rootWindow.removeEventListener('userDataUpdated', refresh);
    }, []);
    const showFluxEntry = Boolean(fluxUrl) && fluxDoctor;
    const fluxEntryHeight = showFluxEntry ? FLUX_ENTRY_HEIGHT : 0;
    const scrollHeight = `calc(${100 / bodyZoom}vh - ${headerHeight + fluxEntryHeight}px)`;

    const drawer = (
        <>
            <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                <Box sx={{ display: 'flex', p: 2, mx: 'auto' }}>
                    <LogoSection />
                </Box>
            </Box>
            <BrowserView>
                <PerfectScrollbar
                    component="div"
                    style={{
                        height: scrollHeight,
                        paddingLeft: '16px',
                        paddingRight: '16px',
                        overflowX: 'hidden'
                    }}
                    options={{ suppressScrollX: true }}
                >
                    <MenuList />
                </PerfectScrollbar>
                {showFluxEntry && <FluxSidebarEntry url={fluxUrl} sticky />}
            </BrowserView>
            <MobileView>
                <Box sx={{ px: 2 }}>
                    <MenuList />
                </Box>
                {showFluxEntry && <FluxSidebarEntry url={fluxUrl} sticky />}
            </MobileView>
        </>
    );

    const container = window !== undefined ? () => window.document.body : undefined;

    return (
        <Box component="nav" sx={{ flexShrink: { md: 0 }, width: matchUpMd ? drawerWidth : 'auto' }} aria-label="mailbox folders">
            <Drawer
                container={container}
                variant={matchUpMd ? 'persistent' : 'temporary'}
                anchor="left"
                open={drawerOpen}
                onClose={drawerToggle}
                sx={{
                    '& .MuiDrawer-paper': {
                        width: drawerWidth,
                        background: theme.palette.background.default,
                        color: theme.palette.text.primary,
                        borderRight: 'none',
                        [theme.breakpoints.up('md')]: {
                            top: '88px'
                        }
                    }
                }}
                ModalProps={{ keepMounted: true }}
                color="inherit"
            >
                {drawer}
            </Drawer>
        </Box>
    );
};

Sidebar.propTypes = {
    drawerOpen: PropTypes.bool,
    drawerToggle: PropTypes.func,
    window: PropTypes.object
};

export default Sidebar;
